import React, { useState } from 'react';
import { Plus, Edit2, Trash2, AlertTriangle, Link, TrendingDown, ChevronDown, ChevronRight, Receipt } from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Modal } from './ui/Modal';
import { Input, Select, Textarea } from './ui/Input';
import { Liability, LiabilityRepayment, Bucket } from '../types';
import { getTotalLiabilities, getNetWorth, getTotalNAV } from '../utils/calculations';
import { formatVND } from '../utils/format';
import { SensitiveValue } from './ui/SensitiveValue';

// DB schema liabilities: id, name, amount (≡ original_amount cũ), interest_rate, lender,
//                        start_date, maturity_date, linked_asset_id, category, fund_id, user_id
const LIABILITY_TYPES = ['Vay mua BĐS', 'Vay kinh doanh', 'Thẻ tín dụng', 'Vay cá nhân', 'Khác'] as const;
const TYPE_COLORS: Record<string, string> = {
  'Vay mua BĐS': '#10b981', 'Vay kinh doanh': '#38bdf8',
  'Thẻ tín dụng': '#f87171', 'Vay cá nhân': '#fbbf24', 'Khác': '#94a3b8',
};

const emptyLiability = (): Partial<Liability> => ({
  name: '', lender: '', type: 'Vay mua BĐS',
  originalBalance: 0, interestRate: 0,
  startDate: new Date().toISOString().split('T')[0],
  maturityDate: '',
});

const emptyRepayment = (): Partial<LiabilityRepayment> => ({
  date: new Date().toISOString().split('T')[0],
  principalPaid: 0,
  interestPaid: 0,
  notes: '',
});

const TH = (align: 'left' | 'right' = 'left'): React.CSSProperties => ({
  padding: '10px 16px', fontSize: 11, fontWeight: 500, color: 'var(--tx-4)',
  textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: align,
  whiteSpace: 'nowrap',
});

export function LiabilityManager() {
  const { state, dispatch } = useAppState();
  const { liabilities = [], repayments = [], assets, buckets = [] } = state;
  const realBuckets = (buckets as Bucket[]).filter(b => b.id !== 'ruong-chua');

  // Liability add/edit modal
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<Liability | null>(null);
  const [form, setForm] = useState<Partial<Liability>>(emptyLiability());

  // Repayment modal
  const [repayLiabilityId, setRepayLiabilityId] = useState<string | null>(null);
  const [repayForm, setRepayForm] = useState<Partial<LiabilityRepayment>>(emptyRepayment());

  // Expandable history rows
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const f = (k: keyof Liability, v: any) => setForm(p => ({ ...p, [k]: v }));
  const rf = (k: keyof LiabilityRepayment, v: any) => setRepayForm(p => ({ ...p, [k]: v }));

  // ── Liability CRUD ──────────────────────────────────────────────────────────
  const handleSaveLiability = () => {
    if (!form.name) return;
    if (editItem) {
      dispatch({ type: 'UPDATE_LIABILITY', payload: { ...editItem, ...form } as Liability });
      setEditItem(null);
    } else {
      dispatch({ type: 'ADD_LIABILITY', payload: form as Omit<Liability, 'id'> });
      setAddOpen(false);
    }
    setForm(emptyLiability());
  };

  const openEdit = (l: Liability) => { setForm({ ...l }); setEditItem(l); };
  const handleDelete = (id: string) => {
    if (window.confirm('Xóa khoản nợ này? Lịch sử trả nợ cũng sẽ bị xóa.')) {
      dispatch({ type: 'DELETE_LIABILITY', payload: id });
      if (expandedId === id) setExpandedId(null);
    }
  };

  // ── Repayment ──────────────────────────────────────────────────────────────
  const openRepay = (liabilityId: string) => {
    setRepayLiabilityId(liabilityId);
    setRepayForm(emptyRepayment());
  };

  const handleSaveRepayment = () => {
    if (!repayLiabilityId || !repayForm.date) return;
    if ((repayForm.principalPaid ?? 0) <= 0 && (repayForm.interestPaid ?? 0) <= 0) return;
    dispatch({
      type: 'ADD_REPAYMENT',
      payload: {
        liabilityId: repayLiabilityId,
        date: repayForm.date!,
        principalPaid: repayForm.principalPaid ?? 0,
        interestPaid: repayForm.interestPaid ?? 0,
        notes: repayForm.notes ?? '',
      },
    });
    setRepayLiabilityId(null);
  };

  const handleDeleteRepayment = (id: string) => {
    if (window.confirm('Xóa lần trả nợ này?')) dispatch({ type: 'DELETE_REPAYMENT', payload: id });
  };


  // ── KPI ─────────────────────────────────────────────────────────────────────
  const totalNAV  = getTotalNAV(assets);
  const totalDebt = getTotalLiabilities(liabilities);
  const netWorth  = getNetWorth(assets, liabilities);
  const debtRatio = totalNAV > 0 ? (totalDebt / totalNAV) * 100 : 0;

  // Khoản nợ đáo hạn sớm nhất
  const now = new Date();
  const soonestMaturity = liabilities
    .filter(l => l.maturityDate)
    .sort((a, b) => new Date(a.maturityDate).getTime() - new Date(b.maturityDate).getTime())[0];
  const maturityLabel = soonestMaturity
    ? new Date(soonestMaturity.maturityDate).toLocaleDateString('vi-VN', { year: 'numeric', month: '2-digit' })
    : '—';
  const maturitySub = soonestMaturity ? soonestMaturity.name : 'Chưa có khoản nào';

  // ── Repayment modal preview ──────────────────────────────────────────────
  const repayingLiability = liabilities.find(l => l.id === repayLiabilityId);
  const previewOutstanding = repayingLiability
    ? Math.max(0, repayingLiability.outstandingBalance - (repayForm.principalPaid ?? 0))
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* KPI row */}
      <div className="rsp-grid-kpi-4">
        <KpiCard label="Tổng Tài sản" value={<SensitiveValue>{formatVND(totalNAV)}</SensitiveValue>} color="#10b981"
          sub={`${assets.length} tài sản`} bg="rgba(16,185,129,0.06)" border="rgba(16,185,129,0.15)" />
        <KpiCard label="Tổng Nợ" value={<SensitiveValue>{formatVND(totalDebt)}</SensitiveValue>} color="#f87171"
          sub={`${liabilities.length} khoản vay`} bg="rgba(239,68,68,0.06)" border="rgba(239,68,68,0.15)" />
        <KpiCard label="Tài sản ròng" value={<SensitiveValue>{formatVND(netWorth)}</SensitiveValue>} color={netWorth >= 0 ? 'var(--tx-1)' : '#f87171'}
          sub={`Tỷ lệ nợ: ${debtRatio.toFixed(1)}%`} bg="var(--bd-sub)" border="rgba(255,255,255,0.07)" />
        <KpiCard label="Đáo hạn sớm nhất" value={maturityLabel} color="#fbbf24"
          sub={maturitySub} bg="rgba(251,191,36,0.06)" border="rgba(251,191,36,0.15)" />
      </div>

      {/* Debt ratio bar */}
      {liabilities.length > 0 && (
        <div style={{ padding: '14px 18px', borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--bd)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--tx-3)' }}>Tỷ lệ đòn bẩy (Debt / Total Assets)</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: debtRatio > 50 ? '#f87171' : debtRatio > 30 ? '#fbbf24' : '#10b981' }}>
              {debtRatio.toFixed(1)}%
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--bd)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3, width: `${Math.min(debtRatio, 100)}%`,
              background: debtRatio > 50 ? 'linear-gradient(90deg,#f87171,#dc2626)' : debtRatio > 30 ? 'linear-gradient(90deg,#fbbf24,#d97706)' : 'linear-gradient(90deg,#10b981,#059669)',
              transition: 'width 0.5s',
            }} />
          </div>
          {debtRatio > 50 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#f87171' }}>
              <AlertTriangle size={12} /> Tỷ lệ nợ cao — cần chú ý quản lý dòng tiền trả nợ
            </div>
          )}
        </div>
      )}

      {/* Table header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, color: 'var(--tx-3)' }}>{liabilities.length} khoản vay / nợ đang theo dõi</span>
        <Button variant="primary" size="sm" icon={<Plus size={13} />} onClick={() => { setForm(emptyLiability()); setAddOpen(true); }}>
          Thêm khoản nợ
        </Button>
      </div>

      {/* Main table */}
      <Card>
        {liabilities.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <TrendingDown size={28} color="var(--tx-5)" style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: 13, color: 'var(--tx-5)' }}>Chưa có khoản nợ nào</div>
            <div style={{ fontSize: 11, color: 'var(--tx-5)', marginTop: 4 }}>Thêm các khoản vay để theo dõi tài sản ròng chính xác</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                  <th style={{ ...TH(), width: 28 }}></th>
                  <th style={TH()}>Khoản nợ</th>
                  <th style={TH()}>Loại</th>
                  <th style={TH('right')}>Nợ gốc</th>
                  <th style={TH('right')}>Đã trả gốc</th>
                  <th style={TH('right')}>Dư nợ</th>
                  <th style={TH('right')}>Lãi suất</th>
                  <th style={TH('right')}>Đáo hạn</th>
                  <th style={TH()}>TS đảm bảo</th>
                  <th style={TH()}></th>
                </tr>
              </thead>
              <tbody>
                {liabilities.map((l, i) => {
                  const linkedAsset = assets.find(a => a.id === l.linkedAssetId);
                  const liabilityRepayments = repayments.filter(r => r.liabilityId === l.id);
                  const totalPaidPrincipal = liabilityRepayments.reduce((s, r) => s + r.principalPaid, 0);
                  const paidPct = l.originalBalance > 0 ? (totalPaidPrincipal / l.originalBalance) * 100 : 0;
                  const isExpanded = expandedId === l.id;
                  const isLast = i === liabilities.length - 1;

                  return (
                    <React.Fragment key={l.id}>
                      {/* Main row */}
                      <tr
                        style={{ borderBottom: isExpanded || !isLast ? '1px solid var(--bd-sub)' : 'none', transition: 'background 0.1s', cursor: 'pointer' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-row-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}>

                        {/* Expand toggle */}
                        <td style={{ padding: '13px 8px 13px 16px' }} onClick={() => setExpandedId(isExpanded ? null : l.id)}>
                          {isExpanded
                            ? <ChevronDown size={14} color="var(--tx-4)" />
                            : <ChevronRight size={14} color="var(--tx-4)" />}
                        </td>

                        {/* Name + lender + progress */}
                        <td style={{ padding: '13px 16px 13px 4px' }} onClick={() => setExpandedId(isExpanded ? null : l.id)}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{l.name}</div>
                          {l.lender && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 2 }}>{l.lender}</div>}
                          <div style={{ marginTop: 6, width: 120 }}>
                            <div style={{ height: 3, borderRadius: 2, background: 'var(--bd)' }}>
                              <div style={{ height: '100%', borderRadius: 2, width: `${paidPct}%`, background: '#10b981' }} />
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--tx-4)', marginTop: 2 }}>Đã trả {paidPct.toFixed(1)}%</div>
                          </div>
                        </td>

                        <td style={{ padding: '13px 16px' }}>
                          <Badge label={l.type || 'Khác'} color={TYPE_COLORS[l.type] ?? '#94a3b8'} size="xs" />
                        </td>
                        <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-3)' }}><SensitiveValue>{formatVND(l.originalBalance)}</SensitiveValue></td>
                        <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 12, color: '#10b981', fontWeight: 600 }}>
                          {totalPaidPrincipal > 0 ? <SensitiveValue>{formatVND(totalPaidPrincipal)}</SensitiveValue> : <span style={{ color: 'var(--tx-5)' }}>—</span>}
                        </td>
                        <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#f87171' }}><SensitiveValue>{formatVND(l.outstandingBalance)}</SensitiveValue></td>
                        <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 13, color: '#fbbf24', fontWeight: 600 }}>{l.interestRate}%</td>
                        <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-3)' }}>
                          {l.maturityDate ? new Date(l.maturityDate).toLocaleDateString('vi-VN', { year: 'numeric', month: '2-digit' }) : '—'}
                        </td>
                        <td style={{ padding: '13px 16px' }}>
                          {linkedAsset ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Link size={11} color="#38bdf8" />
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)' }}>{linkedAsset.name}</div>
                              </div>
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--tx-5)', fontStyle: 'italic' }}>Nợ tín chấp</span>
                          )}
                        </td>
                        <td style={{ padding: '13px 16px' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <IBtn icon={<Receipt size={12} />} color="#10b981" title="Ghi nhận trả nợ" onClick={() => openRepay(l.id)} />
                            <IBtn icon={<Edit2 size={12} />} color="var(--tx-4)" title="Chỉnh sửa" onClick={() => openEdit(l)} />
                            <IBtn icon={<Trash2 size={12} />} color="#f87171" title="Xóa" onClick={() => handleDelete(l.id)} />
                          </div>
                        </td>
                      </tr>

                      {/* Expandable repayment history */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={11} style={{ padding: 0, background: 'var(--bg-app)' }}>
                            <div style={{ padding: '14px 20px 14px 48px', borderBottom: !isLast ? '1px solid var(--bd-sub)' : 'none' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-3)' }}>
                                  Lịch sử trả nợ {liabilityRepayments.length > 0 ? `(${liabilityRepayments.length} lần)` : ''}
                                </span>
                                <Button variant="ghost" size="sm" icon={<Receipt size={12} />} onClick={() => openRepay(l.id)}>
                                  Ghi nhận trả nợ
                                </Button>
                              </div>
                              {liabilityRepayments.length === 0 ? (
                                <div style={{ fontSize: 12, color: 'var(--tx-5)', fontStyle: 'italic' }}>Chưa có lần trả nợ nào được ghi nhận.</div>
                              ) : (
                                <div className="table-wrap">
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                                      <th style={TH()}>Ngày</th>
                                      <th style={TH('right')}>Trả gốc</th>
                                      <th style={TH('right')}>Trả lãi</th>
                                      <th style={TH('right')}>Tổng</th>
                                      <th style={TH()}>Ghi chú</th>
                                      <th style={TH()}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...liabilityRepayments]
                                      .sort((a, b) => b.date.localeCompare(a.date))
                                      .map(r => (
                                        <tr key={r.id} style={{ borderBottom: '1px solid var(--bd-sub)' }}>
                                          <td style={{ padding: '9px 16px', fontSize: 12, color: 'var(--tx-2)' }}>
                                            {new Date(r.date).toLocaleDateString('vi-VN')}
                                          </td>
                                          <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: 12, color: '#10b981', fontWeight: 600 }}>{formatVND(r.principalPaid)}</td>
                                          <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: 12, color: '#fbbf24' }}>{formatVND(r.interestPaid)}</td>
                                          <td style={{ padding: '9px 16px', textAlign: 'right', fontSize: 12, color: 'var(--tx-1)', fontWeight: 600 }}>{formatVND(r.principalPaid + r.interestPaid)}</td>
                                          <td style={{ padding: '9px 16px', fontSize: 12, color: 'var(--tx-4)' }}>{r.notes || '—'}</td>
                                          <td style={{ padding: '9px 16px', textAlign: 'right' }}>
                                            <IBtn icon={<Trash2 size={11} />} color="#f87171" title="Xóa" onClick={() => handleDeleteRepayment(r.id)} />
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>


      {/* ── Add / Edit Liability Modal ─────────────────────────────────────── */}
      <Modal
        open={addOpen || editItem !== null}
        onClose={() => { setAddOpen(false); setEditItem(null); setForm(emptyLiability()); }}
        title={editItem ? 'Chỉnh sửa khoản nợ' : 'Thêm khoản nợ mới'}
        subtitle="Theo dõi đầy đủ để tính Net Worth chính xác"
        size="lg">
        <div className="rsp-modal-grid">
          <Input label="Tên khoản nợ *" value={form.name || ''} onChange={e => f('name', e.target.value)} placeholder="VD: Vay mua BĐS, Vay kinh doanh..." />
          <Input label="Bên cho vay" value={form.lender || ''} onChange={e => f('lender', e.target.value)} placeholder="VD: MB Bank, Techcombank" />
          <Select label="Loại nợ" value={form.type || 'Vay mua BĐS'} onChange={e => f('type', e.target.value as any)}
            options={LIABILITY_TYPES.map(t => ({ value: t, label: t }))} />
          <Select label="Tài sản đảm bảo" value={form.linkedAssetId || ''} onChange={e => f('linkedAssetId', e.target.value || undefined)}
            options={[{ value: '', label: '— Nợ tín chấp (không có TSĐB) —' }, ...assets.map(a => ({ value: a.id, label: `${a.code || a.id} · ${a.name}` }))]} />
          <Select label="Gán vào Quỹ" value={form.fundId || ''} onChange={e => f('fundId', e.target.value || undefined)}
            options={[{ value: '', label: '— Không gán vào quỹ nào —' }, ...realBuckets.map(b => ({ value: b.id, label: b.name }))]} />
          <Input label="Số tiền vay gốc (VNĐ)" type="number" value={form.originalBalance || ''} onChange={e => f('originalBalance', parseFloat(e.target.value) || 0)} />
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 6, fontWeight: 500 }}>Dư nợ hiện tại (tự động)</div>
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-app)', border: '1px solid var(--bd)', fontSize: 13, fontWeight: 700, color: '#f87171' }}>
              {editItem ? formatVND(editItem.outstandingBalance) : formatVND(form.originalBalance ?? 0)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 4 }}>= Nợ gốc − Tổng tiền gốc đã trả</div>
          </div>
          <Input label="Lãi suất (%/năm)" type="number" value={form.interestRate || ''} onChange={e => f('interestRate', parseFloat(e.target.value) || 0)} />
          <Input label="Ngày vay" type="date" value={form.startDate || ''} onChange={e => f('startDate', e.target.value)} />
          <Input label="Ngày đáo hạn" type="date" value={form.maturityDate || ''} onChange={e => f('maturityDate', e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--bd)' }}>
          <Button variant="ghost" onClick={() => { setAddOpen(false); setEditItem(null); }}>Hủy</Button>
          <Button variant="primary" onClick={handleSaveLiability}>{editItem ? 'Lưu thay đổi' : 'Thêm khoản nợ'}</Button>
        </div>
      </Modal>

      {/* ── Repayment Modal ────────────────────────────────────────────────── */}
      <Modal
        open={repayLiabilityId !== null}
        onClose={() => setRepayLiabilityId(null)}
        title="Ghi nhận trả nợ"
        subtitle={repayingLiability ? repayingLiability.name : ''}
        size="md">
        {repayingLiability && (
          <>
            {/* Current outstanding preview */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.15)' }}>
                <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 4 }}>Dư nợ hiện tại</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f87171' }}>{formatVND(repayingLiability.outstandingBalance)}</div>
              </div>
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 4 }}>Dư nợ sau khi trả</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>{formatVND(previewOutstanding)}</div>
              </div>
            </div>
            <div className="rsp-modal-grid">
              <Input label="Ngày trả *" type="date" value={repayForm.date || ''} onChange={e => rf('date', e.target.value)} />
              <div />
              <Input label="Tiền trả gốc (VNĐ)" type="number" value={repayForm.principalPaid || ''} onChange={e => rf('principalPaid', parseFloat(e.target.value) || 0)} placeholder="0" />
              <Input label="Tiền trả lãi (VNĐ)" type="number" value={repayForm.interestPaid || ''} onChange={e => rf('interestPaid', parseFloat(e.target.value) || 0)} placeholder="0" />
            </div>
            <div style={{ marginTop: 14 }}>
              <Textarea label="Ghi chú" value={repayForm.notes || ''} onChange={e => rf('notes', e.target.value)} placeholder="VD: Trả kỳ tháng 3/2025" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--bd)' }}>
              <Button variant="ghost" onClick={() => setRepayLiabilityId(null)}>Hủy</Button>
              <Button variant="primary" onClick={handleSaveRepayment}>Ghi nhận</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function KpiCard({ label, value, color, sub, bg, border }: { label: string; value: React.ReactNode; color: string; sub: string; bg: string; border: string }) {
  return (
    <div style={{ padding: '18px 20px', borderRadius: 14, background: bg, border: `1px solid ${border}` }}>
      <div style={{ fontSize: 11, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, letterSpacing: '-0.02em', marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--tx-4)' }}>{sub}</div>
    </div>
  );
}

function IBtn({ icon, color, onClick, title }: { icon: React.ReactNode; color: string; onClick: () => void; title?: string }) {
  return (
    <button onClick={onClick} title={title}
      style={{ width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none', background: 'var(--bg-card-el)', color, transition: 'background 0.1s' }}
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-row-hover)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}>
      {icon}
    </button>
  );
}
